/* istanbul ignore file */

import { useEffect } from "react";
import { Navigate, useLocation, Outlet } from "react-router-dom";
import { Buffer } from "buffer";
import { SegmentAnalytics } from "@segment/analytics.js-core";
import { usePageViews } from "./hooks";
import { setLastPage } from "./store/commonReducer";
import { useAppDispatch } from "./store/hooks";
import {
  isLoggedIn,
  removeAuthToken,
  REFRESH_TOKEN,
} from "@asayinc/component-library";
import {
  initialState as initialAppConfig,
  useGetAppConfigQuery,
} from "./store/appConfig";

declare global {
  interface Window {
    analytics: SegmentAnalytics.AnalyticsJS;
  }
}

window.Buffer = window.Buffer || Buffer;

const App = (): JSX.Element => {
  usePageViews();
  const { pathname, search } = useLocation();
  const dispatch = useAppDispatch();
  const isNotLoggedIn = !isLoggedIn();
  const { data: { maintenanceMode } = initialAppConfig, isError } =
    useGetAppConfigQuery();

  const isMaintenanceMode = isError
    ? process.env.REACT_APP_MAINTENANCE_MODE === "true"
    : maintenanceMode;

  useEffect(() => {
    if (isMaintenanceMode && !isNotLoggedIn) {
      removeAuthToken();
      window.localStorage.removeItem(REFRESH_TOKEN);
    }
  }, [isMaintenanceMode]);

  // removes double slashes so we can use dynamic routing
  if (pathname.match("/.*/$")) {
    return (
      <Navigate
        replace
        to={{
          pathname: pathname.replace(/\/+$/, ""),
          search,
        }}
      />
    );
  }

  // navigate user to login if they dont have an auth token or maintenance mode is active
  if (
    (isNotLoggedIn || isMaintenanceMode) &&
    pathname !== "/login" &&
    pathname !== "/forgot-password" &&
    pathname !== "/reset-password"
  ) {
    dispatch(setLastPage(pathname));
    return (
      <Navigate
        replace
        to={{
          pathname: "/login",
        }}
      />
    );
  }

  return <Outlet />;
};

export default App;
